import React, { useState, useEffect } from 'react';
import { Button, Input, Card, CardBody, CardHeader, FormGroup, Label, Form, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Table, Alert } from 'reactstrap';
import { api } from "../../config";
import { fetchComAuth, fetchComAuthJson } from '../../common/api';

const TelaConfiguracoesKVA = ({ feiraId , editando}) => {
  const [faixasKVA, setFaixasKVA] = useState([]);
  // Estados do modal
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [faixa_inicial, setfaixa_inicial] = useState('');
  const [faixa_final, setfaixa_final] = useState('');
  const [valor, setvalor] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [tipoCobranca, setTipoCobranca] = useState('metro_quadrado');
  const [erro, setErro] = useState('');

  useEffect(() => {
    const carregarDados = async () => {
      if (!feiraId) return;
  
      try {
        // Buscar os contratos da feira
        const response = await fetchComAuth(`${api.API_URL}/feira/kva/${feiraId}`, {
          method: 'GET'
        })
        
        const data = await response.json();
        setFaixasKVA(data.data);
        
      } catch (err) {
        console.error("Erro ao carregar as faixas de kva da feira", err);
      }
    };
    
    if (editando)
      carregarDados();
  }, [feiraId]);
  
  // Formata número para aceitar vírgula ou ponto como separador decimal
  const formatarNumero = (valor) => {
    if (typeof valor !== 'string') return valor;
    return parseFloat(valor.replace(',', '.')) || 0;
  };

  // Valida se há sobreposição de faixas
  const validarFaixas = (novafaixa_inicial, novafaixa_final) => {
    const inicio = formatarNumero(novafaixa_inicial);
    const fim = novafaixa_final ? formatarNumero(novafaixa_final) : Infinity;
    
    // Verifica se a nova faixa é válida (início <= fim)
    if (fim !== Infinity && inicio > fim) {
      return 'Área inicial não pode ser maior que área final';
    }

    // Verifica sobreposição com outras faixas (exceto a que está sendo editada)
    for (const faixa of faixasKVA) {
      if (faixa.id === editandoId) continue;
      
      const faixaInicio = faixa.faixa_inicial;
      const faixaFim = faixa.faixa_final || Infinity;
      
      if (
        (inicio >= faixaInicio && inicio <= faixaFim) ||
        (fim >= faixaInicio && fim <= faixaFim) ||
        (inicio <= faixaInicio && fim >= faixaFim)
      ) {
        return `Sobreposição com faixa existente (${formatarFaixaArea(faixaInicio, faixaFim)})`;
      }
    }
    
    return '';
  };

  // Limpa o formulário
  const limparFormulario = () => {
    setfaixa_inicial('');
    setfaixa_final('');
    setvalor('');
    setTipoCobranca('metro_quadrado');
    setEditandoId(null);
    setErro('');
    setQuantidade('');
  };

  // Salva uma nova faixa ou atualiza existente
  const handleSalvar = () => {
    const erroValidacao = validarFaixas(faixa_inicial, faixa_final);
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    const novaFaixa = {
      id: editandoId || Math.max(0, ...faixasKVA.map(f => f.id)) + 1,
      faixa_inicial: formatarNumero(faixa_inicial),
      faixa_final: faixa_final ? formatarNumero(faixa_final) : null,
      valor: formatarNumero(valor),
      tipoCobranca,
      quantidade: formatarNumero(quantidade),
    };

    const payload = {
      id: editandoId,
      faixa_inicial: formatarNumero(faixa_inicial),
      faixa_final: faixa_final ? formatarNumero(faixa_final) : null,
      valor: formatarNumero(valor),
      cobra_por_metro: tipoCobranca === 'metro_quadrado',
      feira_id: feiraId,
      quantidade: formatarNumero(quantidade)
    };

    const method = editandoId ? 'PUT' : 'POST';
    fetchComAuthJson(`${api.API_URL}/kva`, {
      method: method,
      body: JSON.stringify(payload)
    })
      .then(res => res.ok ? fetchComAuth(`${api.API_URL}/feira/kva/${feiraId}`, {method: 'GET'}) : Promise.reject('Erro ao salvar')) //, fetch(`${api.API_URL}/feira/kva/${feiraId}`)
      .then(res => res.json())
      .then(data => {
        setFaixasKVA(data.data);
        setModalAberto(false);
        limparFormulario();
      })
      .catch(err => console.error('Erro ao salvar kva:', err));

    setModalAberto(false);
    limparFormulario();
  };

  // Prepara o formulário para edição
  const handleEditar = (faixa) => {
    setfaixa_inicial(faixa.faixa_inicial.toString());
    setfaixa_final(faixa.faixa_final ? faixa.faixa_final.toString() : '');
    setvalor(faixa.valor.toString());
    setTipoCobranca(faixa.tipoCobranca);
    setEditandoId(faixa.id);
    setQuantidade(faixa.quantidade);
    setModalAberto(true);
  };

  // Remove uma faixa
  const handleExcluir = (id) => {
    fetchComAuth(`${api.API_URL}/kva/${id}`, { method: 'DELETE' })
        .then(res => res.ok ? fetchComAuth(`${api.API_URL}/feira/kva/${feiraId}`, { method: 'GET' }) : Promise.reject('Erro ao excluir'))
        .then(res => res.json())
        .then(data => setFaixasKVA(data.data))
        .catch(err => console.error('Erro ao excluir kva:', err));
  };

  // Formata o tipo de cobrança para exibição
  const formatarTipoCobranca = (tipo) => {
    return tipo === 'metro_quadrado' ? 'Por m²' : 'Valor Fixo';
  };

  // Formata a faixa de área para exibição
  const formatarFaixaArea = (inicial, final) => {
    if (final === null) return `${inicial}m² ou mais`;
    return `${inicial}m² - ${final}m²`;
  };

  // Valida entrada numérica (permite números, vírgula e ponto)
  const handleNumericoChange = (valor, setter) => {
    // Remove tudo que não é número, vírgula ou ponto
    const valorFormatado = valor.replace(/[^0-9,.]/g, '');
    // Garante que há no máximo um separador decimal
    const partes = valorFormatado.split(/[,.]/);
    if (partes.length > 2) return;
    
    setter(valorFormatado);
  };

  return (
    <div>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader>
              <Button color="success" onClick={() => { limparFormulario(); setModalAberto(true); }}>
                <i className="ri-add-line align-bottom me-1"></i> Adicionar Faixa KVA
              </Button>
            </CardHeader>
            <CardBody>
              <div className="listjs-table" id="faixasKVALista">
                {faixasKVA.length === 0 ? (
                  <p className="text-muted">Nenhuma faixa de KVA configurada ainda.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>Faixa de Área</th>
                          <th>Tipo de Cobrança</th>
                          <th>Quantidade</th>
                          <th>Valor KVA</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {faixasKVA.map((faixa) => (
                          <tr key={faixa.id}>
                            <td>{formatarFaixaArea(faixa.faixa_inicial, faixa.faixa_final)}</td>
                            <td>{formatarTipoCobranca(faixa.tipoCobranca)}</td>
                            <td>{faixa.quantidade}</td>
                            <td>{faixa.valor.toLocaleString('pt-BR')} KVA</td>
                            <td>
                              <Button color="warning" size="sm" className="me-2" onClick={() => handleEditar(faixa)}>
                                Editar
                              </Button>
                              <Button color="danger" size="sm" onClick={() => handleExcluir(faixa.id)}>
                                Excluir
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modalAberto} toggle={() => setModalAberto(!modalAberto)} centered>
        <ModalHeader toggle={() => setModalAberto(!modalAberto)}>
          {editandoId ? 'Editar Faixa KVA' : 'Nova Faixa KVA'}
        </ModalHeader>
        <ModalBody>
          <Form>
            {erro && <Alert color="danger">{erro}</Alert>}
            
            <FormGroup>
              <Label>Área Inicial (m²)*</Label>
              <Input
                type="text"
                value={faixa_inicial}
                onChange={(e) => handleNumericoChange(e.target.value, setfaixa_inicial)}
                placeholder="Ex: 0 ou 50,5"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Área Final (m²) - Deixe em branco para "ou mais"</Label>
              <Input
                type="text"
                value={faixa_final}
                onChange={(e) => handleNumericoChange(e.target.value, setfaixa_final)}
                placeholder="Ex: 100 ou 150,75"
              />
            </FormGroup>
            <FormGroup>
              <Label>Quantidade*</Label>
              <Input
                type="number"
                value={quantidade}
                onChange={(e) => handleNumericoChange(e.target.value, setQuantidade)}
                placeholder="Ex: 1 ou 5"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Valor KVA*</Label>
              <Input
                type="text"
                value={valor}
                onChange={(e) => handleNumericoChange(e.target.value, setvalor)}
                placeholder="Ex: 10,5 ou 15.75"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Tipo de Cobrança</Label>
              <Input
                type="select"
                value={tipoCobranca}
                onChange={(e) => setTipoCobranca(e.target.value)}
              >
                <option value="metro_quadrado">Por metro quadrado</option>
                <option value="fixo">Valor fixo</option>
              </Input>
            </FormGroup>
            
            <small className="text-muted">* Campos obrigatórios</small>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalAberto(false)}>Cancelar</Button>
          <Button color="primary" onClick={handleSalvar}>
            {editandoId ? 'Atualizar' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TelaConfiguracoesKVA;